import { useCallback, useRef } from 'react';

/**
 * Tiny external store that links whichever media element is currently
 * playing (video, listen-mode audio or an audio session) to UI that lives
 * elsewhere in the tree, such as the transcript panel in the curriculum rail.
 *
 * The element registers itself; readers subscribe to time updates and can
 * seek. Swapping elements (Watch to Listen and back) just re-points the store.
 */
export interface PlaybackController {
  /** Point the store at a media element (or detach with null). */
  attach(el: HTMLMediaElement | null): void;
  /** Detach only if `el` is still the active element. */
  release(el: HTMLMediaElement | null): void;
  getElement(): HTMLMediaElement | null;
  getCurrentTime(): number;
  subscribe(listener: () => void): () => void;
  /** Seek the active element. Starts playback when `play` is true (default). */
  seek(seconds: number, play?: boolean): void;
}

const EVENTS = ['timeupdate', 'seeked', 'loadedmetadata', 'emptied'] as const;

export function createPlaybackController(): PlaybackController {
  let element: HTMLMediaElement | null = null;
  let time = 0;
  const listeners = new Set<() => void>();

  const emit = () => listeners.forEach((l) => l());
  const sync = () => {
    const next = element ? element.currentTime || 0 : 0;
    if (next !== time) {
      time = next;
      emit();
    }
  };

  const attach = (el: HTMLMediaElement | null) => {
    if (el === element) return;
    if (element) EVENTS.forEach((e) => element?.removeEventListener(e, sync));
    element = el;
    if (element) EVENTS.forEach((e) => element?.addEventListener(e, sync));
    sync();
  };

  return {
    attach,
    release(el) {
      if (el && el === element) attach(null);
    },
    getElement: () => element,
    getCurrentTime: () => time,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    seek(seconds, play = true) {
      const el = element;
      if (!el) return;
      const target = Math.max(0, seconds);
      try {
        el.currentTime = target;
      } catch {
        /* not seekable yet */
      }
      time = target;
      emit();
      if (play && el.paused) el.play().catch(() => { /* autoplay blocked */ });
    },
  };
}

/**
 * Callback ref that registers a media element with a controller while it is
 * mounted. Safe to call with no controller (does nothing).
 */
export function useBindMediaElement(controller?: PlaybackController | null) {
  const nodeRef = useRef<HTMLMediaElement | null>(null);
  return useCallback(
    (node: HTMLMediaElement | null) => {
      if (nodeRef.current && nodeRef.current !== node) controller?.release(nodeRef.current);
      nodeRef.current = node;
      if (node) controller?.attach(node);
    },
    [controller],
  );
}
